import { useState, type KeyboardEvent } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { BookingCandidate } from "@/app/booking-calendar/api/reviewApi"
import { useGetBookingQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { AppointmentDialog } from "@/app/booking-calendar/components/modals"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { cn } from "@/lib/utils"
import { appointmentStartLabel } from "./reviewBookingSummary"

/**
 * One existing appointment the matcher found (AB#797): its confirmation number, client name and
 * Pacific start, read from that booking when the review was opened. The whole card opens it in the
 * same dialog the calendar, Search and the processed list use. A booking with nothing in the
 * scheduling system has nothing to open, so its card is not a button. The band and the agreed keys stay.
 */
export function ExistingAppointmentCard({ candidate, band }: { candidate: BookingCandidate; band: string | number }) {
  const [open, setOpen] = useState(false)
  const uid = candidate.matchedCalBookingUid ?? null
  const booking = useGetBookingQuery(uid ?? "", { skip: !open || !uid })

  // Already open (loading, shown or failed): another click does nothing until it closes.
  const show = () => {
    if (uid && !open) setOpen(true)
  }
  // Only keys pressed on the card itself.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      show()
    }
  }
  const label = `Open appointment ${candidate.matchedConfirmationCode || candidate.matchedAttendeeName || ""}`.trim()

  // The card, its error row and the dialog stay together in the list; only the card is a button.
  return (
    <div>
      <div
        className={cn(
          "rounded-lg border p-3 text-sm",
          uid && "cursor-pointer transition-colors hover:bg-muted/50 hover:ring-1 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
        data-testid="existing-appointment-candidate"
        role={uid ? "button" : undefined}
        tabIndex={uid ? 0 : undefined}
        aria-label={uid ? label : undefined}
        onClick={uid ? show : undefined}
        onKeyDown={uid ? onKeyDown : undefined}
      >
        <div className="flex items-center justify-between">
          <span className="font-medium">Existing appointment</span>
          <Badge variant="outline">{band}</Badge>
        </div>

        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <dt className="text-muted-foreground">Confirmation Number</dt>
          <dd className="font-mono" data-testid="existing-appointment-code">{candidate.matchedConfirmationCode || "—"}</dd>
          <dt className="text-muted-foreground">Client Name</dt>
          <dd data-testid="existing-appointment-name">{candidate.matchedAttendeeName || "—"}</dd>
          <dt className="text-muted-foreground">Start Time</dt>
          <dd data-testid="existing-appointment-start">{appointmentStartLabel(candidate.matchedSlotStart) ?? "—"}</dd>
        </dl>

        {!uid && <div className="mt-1 text-muted-foreground">Not in the scheduling system</div>}
        <div className="text-muted-foreground">Agreed on: {candidate.matchedFields}</div>
      </div>

      {/* Outside the card: a control inside a button is never announced, and its clicks must not reopen it. */}
      {open && booking.isError && (
        <div className="mt-1 flex items-center justify-between gap-2 text-sm">
          <span className="text-destructive">That appointment could not be loaded from the scheduling system.</span>
          <Button
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.stopPropagation()
              setOpen(false)
            }}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Outside the card too, so clicks and keys inside the dialog never reach the card's handlers. */}
      {open && (
        <AppointmentDialog
          booking={booking.data}
          staffName={undefined}
          timeZone={SITE_TIME_ZONE}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  )
}
